import React, { FC, useState, useEffect } from 'react'
import isEqual from 'lodash/isEqual'
import { IAutocompleteOption } from 'transfix-ui/components/Autocomplete'
import { Avatar } from 'transfix-ui/components/Avatar'
import { Button } from 'transfix-ui/components/Button'
import { Box, BoxProps } from 'transfix-ui/core'
import {
  ShipmentIssueSubscriber,
  ShipmentIssueUpdate,
  ShipmentIssueTypeDefaultSubscriber,
  ShipmentIssueSubscriberCreate,
} from 'apps/marketplace/apiTypes/shipmentIssues'
import TagAutosuggest from 'apps/marketplace/components/TagAutosuggest'
import { RemoveIcon } from 'transfix-icons'

interface ITagSubscribersProps {
  value?: IAutocompleteOption[]
  onUpdateSubscribers: (data: ShipmentIssueUpdate) => void
  OuterBoxProps?: BoxProps
  /**
   * Whether or not 'onClickAddHandler' will be triggered on change.
   * This removes the 'Add' button since the add functionality will be triggered
   * as soon as a subscriber is chosen.
   */
  autoAddOnChange?: boolean
  placeholder?: string
  hideTags?: boolean
}

export const mapSubscriberValue = (
  val: ShipmentIssueSubscriber | ShipmentIssueTypeDefaultSubscriber
): IAutocompleteOption => ({
  groupIndex: 1,
  groupName: val.subscriberType,
  id: String(val.subscriberId),
  text: `${val.firstName} ${val.lastName}`,
})

export interface SubscriberValueCreate extends ShipmentIssueSubscriberCreate {
  text?: string
}

export interface SubscriberMapValue {
  id?: string
  text?: string
  type?: string
  groupName?: string
}

const mapSubscriberValueEvent = (val: SubscriberMapValue): SubscriberValueCreate => ({
  id: Number(val.id),
  type: val.type || val.groupName || 'User',
  text: val.text,
})

/**
 * Tag subscribers to follow issue status + changes
 */
const TagSubscribers: FC<ITagSubscribersProps> = ({
  value = [],
  onUpdateSubscribers,
  OuterBoxProps = {},
  autoAddOnChange = false,
  placeholder = 'Search for user or group',
  hideTags = false,
}) => {
  const defaultSelected = value.length > 0 ? value : []

  const [selected, setSelected] = useState<Partial<IAutocompleteOption> | undefined>(undefined)
  const [tags, setTags] = useState<Partial<IAutocompleteOption>[]>(defaultSelected)

  // Update when outside value of control also changed
  // So list is updated inside of the state component too
  useEffect(() => {
    // Avoid circular loop and checks if value need to be mapped
    // If value don't needs to be mapped then it the same one as before
    if (!isEqual(value, tags)) {
      setTags(value)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value])

  const changeSelectHandler = (option?: Partial<IAutocompleteOption>) => setSelected(option)

  const onClickAddHandler = (option?: Partial<IAutocompleteOption>) => {
    const selectedOption = autoAddOnChange ? option : selected
    if (selectedOption?.id) {
      // If selected tag is already in tags list, do not update list
      if (tags.find(tag => String(tag.id) === String(selectedOption.id))) return

      const subscribersOptions = [...tags, selectedOption]
      const subscribers = subscribersOptions.map(mapSubscriberValueEvent)
      onUpdateSubscribers({ subscribers })
    }

    setSelected(undefined)
  }

  const onClickRemoveHandler = (ev: React.MouseEvent) => {
    ev.preventDefault()
    // eslint-disable-next-line no-unused-expressions
    ev.currentTarget.parentElement?.parentElement?.classList.add('fadeOut')
    const tagsFiltered = tags.filter(tag => String(ev.currentTarget.id) !== String(tag.id))

    onUpdateSubscribers({
      subscribers: tagsFiltered?.map(mapSubscriberValueEvent),
    })
  }

  return (
    <>
      <Box display='flex' {...OuterBoxProps}>
        <Box mr={1} width='100%'>
          <TagAutosuggest
            value={selected}
            onChange={autoAddOnChange ? onClickAddHandler : changeSelectHandler}
            placeholder={placeholder}
            withAvatar
          />
        </Box>
        {!autoAddOnChange && (
          <Button disabled={!selected} color='primary' onClick={onClickAddHandler} className='w-40'>
            Add
          </Button>
        )}
      </Box>
      {tags?.length > 0 && !hideTags && (
        <Box mt={3}>
          {tags.map(tag => (
            <div key={tag.id} className='fadeIn'>
              <Box mt={3} display='flex' alignItems='center' justifyContent='space-between'>
                <Box display='flex' alignItems='center'>
                  <Avatar size='xsmall' name={tag.text} />
                  <Box ml={2}>{tag.text}</Box>
                </Box>
                <Box>
                  <Button
                    startIcon={<RemoveIcon />}
                    type='submit'
                    id={tag.id}
                    onClick={onClickRemoveHandler}>
                    Remove
                  </Button>
                </Box>
              </Box>
            </div>
          ))}
        </Box>
      )}
    </>
  )
}

export default TagSubscribers
